import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { MenuItem, MediaAsset } from "@/types/menu";
import { useToast } from "@/hooks/use-toast";
import { translateMenu } from "@/lib/gemini/client";
import { useUserStore } from "@/store/userStore";
import useMenuStore from "@/store/menuStore";
import { cn } from "@/lib/utils";
import { Trash2, ImageIcon, Upload } from "lucide-react";
import { getLanguageInfo } from "@/lib/languages";
import { createClient } from "@/lib/supabase/client";
import { useDropzone } from "react-dropzone";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import * as flags from "country-flag-icons/react/3x2";
import { trackEvent } from "@/lib/mixpanel-client";

type MenuItemEditDialogProps = {
  item: MenuItem;
  language: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedItem: MenuItem) => void;
  onDelete: () => void;
  supportedLanguages?: string[];
  isNewItem?: boolean;
  categoryId: string;
  currency?: string;
  menuId: string; // Required for saving images
  menu: { mediaAssets?: MediaAsset[] }; // Needed to display existing images
};

export default function MenuItemEditDialog({
  item,
  language,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  supportedLanguages = [],
  isNewItem = false,
  categoryId,
  currency = "¥",
  menuId,
  menu,
}: MenuItemEditDialogProps) {
  const { toast } = useToast();
  const { getRemainingCredits } = useUserStore();
  const remainingCredits = getRemainingCredits();
  const supabase = createClient();
  const addMediaToItem = useMenuStore((state) => state.addMediaToItem);
  const deleteMediaAsset = useMenuStore((state) => state.deleteMediaAsset);
  const t = useTranslations("MenuItemEditDialog");
  const tLang = useTranslations("LanguageNames");

  const [originalMediaAssetId, setOriginalMediaAssetId] = useState<
    string | null
  >(null);

  const [editedItem, setEditedItem] = useState<MenuItem>({ ...item });
  const [showTaxIncluded, setShowTaxIncluded] = useState(
    item.price.taxIncluded !== undefined
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [shouldTranslate, setShouldTranslate] = useState(remainingCredits > 0);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(() => {
    if (remainingCredits > 0) {
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      return availableLanguages.slice(0, remainingCredits);
    }
    return [];
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showCancelTranslationAlert, setShowCancelTranslationAlert] =
    useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Find existing media asset if any
  const existingMediaAsset =
    item.mediaIds?.length && menu?.mediaAssets?.length
      ? menu.mediaAssets.find((asset) => asset.id === item.mediaIds?.[0])
      : undefined;

  // Maximum file size (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // Handle file validation
  const validateFile = useCallback(
    (file: File) => {
      // Validate file is an image
      if (!file.type.startsWith("image/")) {
        return {
          valid: false,
          error: t("imageUploadFailed"),
        };
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        return {
          valid: false,
          error: "Image must be less than 5MB",
        };
      }

      return { valid: true, error: null };
    },
    [MAX_FILE_SIZE, t]
  );

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const validation = validateFile(file);

        if (!validation.valid) {
          toast({
            variant: "destructive",
            title: t("imageUploadFailed"),
            description: validation.error,
          });
          return;
        }

        // Revoke the previous preview URL *before* setting the new one
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setHasChanges(true);
      }
    },
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxFiles: 1,
    multiple: false,
  });

  // Add a separate effect for cleaning up the previewUrl on unmount
  useEffect(() => {
    // Return a cleanup function
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]); // This effect ONLY depends on previewUrl for cleanup

  // Redefined helper function to check if there are any pending changes
  const checkForChanges = useCallback(() => {
    // Check if a new file has been selected
    if (selectedFile) return true;

    // Check if an existing image has been marked for removal (mediaIds cleared)
    if (originalMediaAssetId && editedItem.mediaIds?.length === 0) return true;

    // Compare current edited text fields with the original item prop
    if (editedItem.name[language] !== (item.name[language] || "")) return true;
    if (
      (editedItem.description?.[language] || "") !==
      (item.description?.[language] || "")
    )
      return true;

    // Compare price and tax setting
    if (editedItem.price.amount !== item.price.amount) return true;
    if (showTaxIncluded !== (item.price.taxIncluded !== undefined)) return true;

    // Compare translation settings (shouldTranslate toggle)
    // A simple check to see if the desire to translate has changed
    const initialShouldTranslateCheck = remainingCredits > 0;
    if (shouldTranslate !== initialShouldTranslateCheck) {
      // Consider it a change only if activating translations or deactivating when languages were selected
      // This prevents enabling save just by toggling translate off when it was already off
      if (shouldTranslate || selectedLanguages.length > 0) {
        return true;
      }
    }

    // Add check for selected languages change if needed for your definition of "changes"

    return false;
  }, [
    selectedFile,
    originalMediaAssetId,
    editedItem,
    item,
    language,
    showTaxIncluded,
    shouldTranslate,
    remainingCredits,
    selectedLanguages, // Need selectedLanguages if comparing its changes
  ]);

  // Reset state when the dialog opens or the base item ID changes
  useEffect(() => {
    setOriginalMediaAssetId(item.mediaIds?.[0] || null);
    setEditedItem({ ...item });
    setShowTaxIncluded(item.price.taxIncluded !== undefined);

    // Reset file state ONLY when dialog opens or item ID changes
    // Keep existing preview if just re-rendering for the same item while open
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);

    setHasChanges(false);
    setIsSaving(false);
    setIsTranslating(false);
    setUploadProgress(0);
    setIsUploading(false);

    // Reset translation states based on available credits
    if (remainingCredits > 0) {
      setShouldTranslate(true);
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      setSelectedLanguages(availableLanguages.slice(0, remainingCredits));
    } else {
      setShouldTranslate(false);
      setSelectedLanguages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, isOpen]); // Depend only on item.id and isOpen for major resets

  // Effect specifically for resetting input fields if language changes while open
  useEffect(() => {
    if (isOpen) {
      // Only run if the dialog is already open
      setEditedItem((prev) => ({ ...prev, ...item })); // Merge potentially updated item props
      // Add other field resets if necessary based on language or other props changing *while open*
    }
  }, [item, language, supportedLanguages, remainingCredits, isOpen]); // Keep dependencies here for mid-dialog updates

  const handlePriceChange = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");
    const amount = numericValue ? parseInt(numericValue, 10) : 0;

    setEditedItem((prev) => ({
      ...prev,
      price: {
        ...prev.price,
        amount: amount,
      },
    }));
    setHasChanges(true);
  };

  // Update the tax included toggle handler
  const handleTaxIncludedChange = (checked: boolean) => {
    setShowTaxIncluded(checked);
    setHasChanges(checkForChanges());
  };

  // Update the name change handler
  const handleInputChange = (field: string, value: string) => {
    setEditedItem((prev) => {
      const updated = { ...prev };
      if (field === "name") {
        updated.name = { ...updated.name, [language]: value };
      } else if (field === "description") {
        if (!updated.description) {
          updated.description = {};
        }
        updated.description = { ...updated.description, [language]: value };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const handleTranslateToggle = (checked: boolean) => {
    if (checked && remainingCredits === 0) {
      toast({
        variant: "destructive",
        title: t("noCreditTitle"),
        description: t("noCreditDesc"),
      });
      return;
    }
    setShouldTranslate(checked);
    if (checked) {
      // When enabling translation, only select languages up to the remaining credits
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      const languagesToSelect = availableLanguages.slice(0, remainingCredits);
      setSelectedLanguages(languagesToSelect);
      setHasChanges(checkForChanges());

      // Show a warning if there are more languages available than credits
      if (availableLanguages.length > remainingCredits) {
        toast({
          variant: "default",
          title: t("limitedCreditsTitle"),
          description: t("limitedCreditsDescription", {
            count: remainingCredits,
            plural: remainingCredits === 1 ? "" : "s",
          }),
        });
      }
    } else {
      // When disabling translation, clear selected languages
      setSelectedLanguages([]);
      setHasChanges(checkForChanges());
    }
  };

  // Calculate required credits for translations
  const requiredCredits = supportedLanguages.length - 1; // Exclude current language

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    let translationAttempted = false;
    let translationSucceeded = true; // Assume success

    // Create a new AbortController for this save operation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let assetToDeleteId: string | null = null;
    let newMediaAsset: MediaAsset | null = null;

    try {
      const updatedItem = { ...editedItem };

      // Update the price object based on tax toggle
      if (showTaxIncluded) {
        updatedItem.price = {
          amount: updatedItem.price.amount,
          taxIncluded: Math.round(updatedItem.price.amount * 1.1),
        };
      } else {
        const { amount } = updatedItem.price;
        updatedItem.price = { amount };
      }

      // Preserve original translations before attempting new ones
      const originalName = item.name[language] || "";
      const originalDescription = item.description?.[language] || "";
      const originalNameTranslations = { ...item.name };
      const originalDescriptionTranslations = item.description
        ? { ...item.description }
        : undefined;

      // Initialize name and description with only the current language's edited value
      updatedItem.name = { [language]: updatedItem.name[language] };
      if (updatedItem.description?.[language]?.trim()) {
        // Only include description if it has content
        updatedItem.description = {
          [language]: updatedItem.description[language],
        };
      } else {
        updatedItem.description = undefined; // Set to undefined if description is empty
      }

      // Track what fields were updated
      const updatedFields = [];
      if (updatedItem.name[language] !== originalName)
        updatedFields.push("Name");
      if ((updatedItem.description?.[language] || "") !== originalDescription)
        updatedFields.push("Description");
      if (updatedItem.price.amount !== item.price.amount)
        updatedFields.push("Price");
      if (showTaxIncluded !== (item.price.taxIncluded !== undefined))
        updatedFields.push("Tax Setting");

      // --- Image Handling ---
      const isRemovingImage =
        originalMediaAssetId &&
        editedItem.mediaIds?.length === 0 &&
        !selectedFile;
      const isReplacingImage = originalMediaAssetId && selectedFile;

      if (isReplacingImage || selectedFile) {
        updatedFields.push("Image");
        // Upload new image
        try {
          setIsUploading(true);
          // Generate unique ID for the media asset
          const mediaAssetId = `media-${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 9)}`;

          // Create storage path for the file
          const filename = `${mediaAssetId}-${selectedFile.name.replace(
            /[^a-zA-Z0-9_.]/g,
            "_"
          )}`;
          const storagePath = `menus/${menuId}/items/${updatedItem.id}/${filename}`;

          // Track image upload start
          trackEvent("Menu Item Image Upload Started", {
            "Item ID": updatedItem.id,
            "Image Type": selectedFile.type,
            "Image Size": selectedFile.size,
          });

          // Upload the file to Supabase Storage
          const { error: uploadError } = await supabase.storage
            .from("menus")
            .upload(storagePath, selectedFile);

          if (uploadError) {
            throw new Error(`Upload failed: ${uploadError.message}`);
          }

          // Simulate progress updates (in a real app, use proper upload progress API)
          for (let i = 0; i <= 100; i += 10) {
            setUploadProgress(i);
            if (i < 100) {
              await new Promise((resolve) => setTimeout(resolve, 50));
            }
          }

          // Get the public URL
          const {
            data: { publicUrl },
          } = supabase.storage.from("menus").getPublicUrl(storagePath);

          // Track image upload success
          trackEvent("Menu Item Image Upload Completed", {
            "Item ID": updatedItem.id,
          });

          // After successful upload, set newMediaAsset
          newMediaAsset = {
            id: mediaAssetId,
            url: publicUrl,
            type: "image",
            filename: selectedFile.name,
            uploadedAt: new Date().toISOString(),
          };
          updatedItem.mediaIds = [newMediaAsset.id]; // Associate new ID immediately
          assetToDeleteId = originalMediaAssetId; // Mark old asset for deletion
          setIsUploading(false);
          // DON'T clear selectedFile/previewUrl here
        } catch (error: unknown) {
          console.error("Image upload error:", error);
          toast({
            variant: "destructive",
            title: t("imageUploadFailed"),
            description:
              error instanceof Error
                ? error.message
                : "Failed to upload image. Please try again.",
          });

          // Track image upload failure
          trackEvent("Menu Item Image Upload Failed", {
            "Item ID": updatedItem.id,
            Error: error instanceof Error ? error.message : "Unknown error",
          });

          setIsSaving(false);
          setIsUploading(false);
          return;
        }
      } else if (isRemovingImage) {
        updatedFields.push("Image Removed");
        // Just mark the old asset for deletion if removing
        assetToDeleteId = originalMediaAssetId;
        updatedItem.mediaIds = []; // Ensure mediaIds is empty
      }
      // --- End Image Handling ---

      // Track the item update
      if (updatedFields.length > 0) {
        trackEvent("Menu Item Updated", {
          "Item ID": updatedItem.id,
          "Category ID": categoryId,
          "Updated Fields": updatedFields,
          "Is New Item": isNewItem,
        });
      }

      // Check if text has actually changed in the current language
      const textHasChanged =
        updatedItem.name[language] !== originalName ||
        (updatedItem.description?.[language] || "") !== originalDescription;

      // Only attempt translations if shouldTranslate, languages selected, credits available, AND text changed
      if (
        supportedLanguages.length > 0 &&
        shouldTranslate &&
        selectedLanguages.length > 0 &&
        getRemainingCredits() > 0 &&
        textHasChanged
      ) {
        translationAttempted = true;
        setIsTranslating(true);

        // Track translation start
        trackEvent("Menu Item Translation Started", {
          "Item ID": updatedItem.id,
          "Source Language": language,
          "Target Languages": selectedLanguages,
        });

        // Create a temporary menu structure for the translation API
        const tempMenu = {
          id: "temp",
          title: { [language]: "Item Translation Context" }, // Minimal context
          categories: [
            {
              id: categoryId || "temp_cat",
              name: { [language]: "Category Context" },
              items: [
                {
                  ...updatedItem, // Pass the current state of the item
                  // Ensure only source language text is sent for name/description
                  name: { [language]: updatedItem.name[language] },
                  description: updatedItem.description
                    ? { [language]: updatedItem.description[language] }
                    : undefined,
                  // Include price and category for context if needed by API, otherwise can simplify
                  price: updatedItem.price,
                  category: categoryId || "temp_cat",
                },
              ],
            },
          ],
          supportedLanguages: [language],
        };

        // Apply translations to the updated menu item
        try {
          const { categories: translatedCategories } = await translateMenu(
            tempMenu,
            selectedLanguages.join(",")
          );

          if (signal.aborted) throw new Error("Translation aborted");

          // Extract the translated item from the result
          if (translatedCategories?.[0]?.items?.[0]) {
            const translatedItem = translatedCategories[0].items[0];

            // Add translated name and description to the updated item
            for (const targetLang of selectedLanguages) {
              if (translatedItem.name[targetLang]) {
                updatedItem.name[targetLang] = translatedItem.name[targetLang];
              }
              if (translatedItem.description?.[targetLang]) {
                if (!updatedItem.description) updatedItem.description = {};
                updatedItem.description[targetLang] =
                  translatedItem.description[targetLang];
              }
            }
          }

          // Track translation completion
          trackEvent("Menu Item Translation Completed", {
            "Item ID": updatedItem.id,
            "Source Language": language,
            "Target Languages": selectedLanguages,
          });

          // Track credits consumed
          trackEvent("Credits Consumed", {
            "Credits Used": selectedLanguages.length,
            Feature: "Item Translation",
            Languages: selectedLanguages,
          });
        } catch (error: unknown) {
          if (!signal.aborted) {
            console.error("Translation error:", error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Unknown translation error";
            toast({
              variant: "destructive",
              title: t("translationError"),
              description: errorMessage,
            });
            translationSucceeded = false;

            // Track translation failure
            trackEvent("Menu Item Translation Failed", {
              "Item ID": updatedItem.id,
              "Source Language": language,
              "Target Languages": selectedLanguages,
              Error: errorMessage,
            });
          }
        } finally {
          setIsTranslating(false);
        }
      }

      // Add any existing translations back that weren't part of the current translation
      updatedItem.name = { ...originalNameTranslations, ...updatedItem.name };
      if (originalDescriptionTranslations || updatedItem.description) {
        updatedItem.description = {
          ...originalDescriptionTranslations,
          ...updatedItem.description,
        };
      }

      // Call the update handler to update the parent component
      onUpdate(updatedItem);

      // Handle media asset management separately
      try {
        if (newMediaAsset) {
          // Save the new media asset to Supabase and the menu store
          await addMediaToItem(
            menuId,
            categoryId,
            updatedItem.id,
            newMediaAsset,
            supabase
          );
        }

        if (assetToDeleteId) {
          // Delete the old media asset from Supabase and the menu store
          await deleteMediaAsset(menuId, assetToDeleteId, supabase);

          // Track media deletion
          trackEvent("Menu Item Media Deleted", {
            "Item ID": updatedItem.id,
            "Media ID": assetToDeleteId,
          });
        }
      } catch (error: unknown) {
        console.error("Error managing media assets:", error);
        // Already saved the item data, so just show a warning about media
        toast({
          variant: "default", // Less severe than destructive
          title: t("mediaWarning"),
          description: t("mediaWarningDesc"),
        });
      }

      // Close the dialog
      onClose();

      // Show appropriate toast message
      if (translationAttempted) {
        if (translationSucceeded) {
          toast({
            title: isNewItem
              ? t("itemAddedTranslated")
              : t("itemUpdatedTranslated"),
            description: t("changesSuccessfullyTranslated"),
          });
        } else {
          toast({
            title: isNewItem
              ? t("partialTranslationTitle")
              : t("partialTranslationUpdateTitle"),
            description: t("partialTranslationDesc"),
            variant: "default",
          });
        }
      } else {
        toast({
          title: isNewItem ? t("itemAdded") : t("itemUpdated"),
          description: t("changesSuccessfullySaved"),
        });
      }
    } catch (error: unknown) {
      // Handle any other errors
      console.error("Error saving item:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast({
        variant: "destructive",
        title: t("saveError"),
        description: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add a function to handle translation cancellation
  const handleCancelTranslation = () => {
    // Abort the translation if in progress
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsTranslating(false);
    setShowCancelTranslationAlert(false);
    onClose();

    toast({
      title: t("translationCancelled"),
      description: t("translationCancelledDesc"),
    });
  };

  // Update the dialog open change handler
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      // If dialog is closing and we have unsaved changes
      if (hasChanges && isSaving) {
        // Prevent closing if we're in the middle of saving
        return;
      }

      // Safety check for ongoing API operations
      if (isTranslating) {
        setShowCancelTranslationAlert(true);
        return;
      }

      // Close the dialog
      onClose();
    }
  };

  // Handler for the Cancel button
  const handleCancel = () => {
    if (isTranslating) {
      setShowCancelTranslationAlert(true);
      return;
    }
    onClose();
  };

  // Helper function to get country code for a language
  const getCountryCode = (languageCode: string): string => {
    // Special cases for Mandarin variants
    if (
      languageCode.toLowerCase() === "chinese_simplified" ||
      languageCode.toLowerCase() === "simplified_chinese" ||
      languageCode.toLowerCase() === "chinese-simplified"
    ) {
      return "CN"; // China flag for Simplified Chinese
    }
    if (
      languageCode.toLowerCase() === "chinese_traditional" ||
      languageCode.toLowerCase() === "traditional_chinese" ||
      languageCode.toLowerCase() === "chinese-traditional"
    ) {
      return "TW"; // Taiwan flag for Traditional Chinese
    }

    const countryMap: Record<string, string> = {
      arabic: "SA",
      bhojpuri: "IN",
      cantonese: "HK",
      chinese: "CN", // Generic Chinese
      chinese_simplified: "CN",
      chinese_traditional: "TW",
      czech: "CZ",
      dutch: "NL",
      english: "GB",
      filipino: "PH",
      french: "FR",
      german: "DE",
      hebrew: "IL",
      hindi: "IN",
      indonesian: "ID",
      italian: "IT",
      japanese: "JP",
      korean: "KR",
      malay: "MY",
      mandarin: "CN", // Generic Mandarin
      mandarin_simplified: "CN",
      mandarin_traditional: "TW",
      persian: "IR",
      polish: "PL",
      portuguese: "PT",
      portuguese_brasil: "BR",
      portuguese_brasileiro: "BR",
      português_brasil: "BR",
      português_brasileiro: "BR",
      romanian: "RO",
      russian: "RU",
      simplified_chinese: "CN",
      slovenian: "SI",
      spanish: "ES",
      swedish: "SE",
      thai: "TH",
      traditional_chinese: "TW",
      turkish: "TR",
      vietnamese: "VN",
    };

    // Special check for Brazilian Portuguese variants
    if (
      languageCode.toLowerCase().includes("portuguese") ||
      languageCode.toLowerCase().includes("português")
    ) {
      if (
        languageCode.toLowerCase().includes("brasil") ||
        languageCode.toLowerCase().includes("brazil") ||
        languageCode.toLowerCase().includes("brasileiro")
      ) {
        return "BR";
      }
    }

    return countryMap[languageCode.toLowerCase()] || "US"; // Default to US flag
  };

  // Get localized language name
  const getLocalizedLanguageName = (code: string): string => {
    // Special cases for languages with variants
    if (code === "chinese-simplified" || code === "chinese_simplified") {
      return tLang("chinese-simplified");
    }
    if (code === "chinese-traditional" || code === "chinese_traditional") {
      return tLang("chinese-traditional");
    }

    // Try to get translation from the LanguageNames namespace
    try {
      return tLang(code.toLowerCase());
    } catch {
      // Fallback to the language name from getLanguageInfo if translation is missing
      return getLanguageInfo(code)?.label || code;
    }
  };

  // Handle delete button click - shows confirmation dialog
  const handleDeleteClick = () => {
    setShowDeleteAlert(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = () => {
    // Track the item deletion
    trackEvent("Menu Item Deleted", {
      "Item ID": item.id,
      "Category ID": categoryId,
    });

    onDelete();
    setShowDeleteAlert(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="p-0 md:max-h-[calc(100vh-40px)] w-dvw sm:w-auto h-dvh sm:h-auto flex flex-col max-w-full sm:min-w-[600px] overflow-hidden gap-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle>
              {isNewItem ? t("addMenuItem") : t("editMenuItem")}
            </DialogTitle>
            <DialogDescription>{t("description")}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-6 px-6 overflow-y-auto">
            {/* Item Image Section - Moved above the Name */}
            <div className="grid gap-2">
              <Label htmlFor="image">{t("itemImage")}</Label>
              <div
                {...getRootProps()}
                className={cn(
                  "border-2 border-dashed rounded-md p-4 transition-colors cursor-pointer",
                  isDragActive
                    ? "border-primary/70 bg-primary/5"
                    : "border-muted-foreground/30 hover:border-muted-foreground/50"
                )}
              >
                <input {...getInputProps()} />

                {previewUrl ||
                (existingMediaAsset &&
                  !editedItem.mediaIds?.length === false) ? (
                  // Show selected or existing image
                  <div className="relative w-full aspect-video flex items-center justify-center">
                    <Image
                      src={previewUrl || existingMediaAsset?.url || ""}
                      alt="Menu item"
                      fill
                      className="object-contain"
                      sizes="(max-width: 768px) 100vw, 500px"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Clean up preview URL if it exists
                        if (previewUrl) {
                          URL.revokeObjectURL(previewUrl);
                          setPreviewUrl(null); // Clear the state
                        }
                        // Clear selected file
                        setSelectedFile(null);

                        // Remove mediaIds from the edited item to disassociate the image
                        setEditedItem((prev) => ({
                          ...prev,
                          mediaIds: [], // Clear the mediaIds array
                        }));

                        setHasChanges(true);
                      }}
                      className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full hover:bg-black/80 transition-colors"
                      type="button"
                      aria-label="Remove image"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  // Empty state
                  <div className="text-center py-8">
                    {isDragActive ? (
                      <div className="flex flex-col items-center space-y-2">
                        <Upload className="h-10 w-10 text-primary" />
                        <p>{t("dropImage")}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center space-y-2">
                        <ImageIcon className="h-10 w-10 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {t("dragDropImage")}
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2"
                        >
                          {t("chooseFile")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {isUploading && (
                  <div className="mt-4">
                    <Progress value={uploadProgress} className="h-1" />
                    <p className="text-xs text-center mt-1">
                      {t("uploading", { progress: uploadProgress })}
                    </p>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("imageRecommendation")}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">{t("name")}</Label>
              <Input
                id="name"
                value={
                  editedItem.name[language] ||
                  Object.values(item.name)[0] ||
                  t("unnamed")
                }
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder={t("namePlaceholder")}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">{t("description_field")}</Label>
              <Textarea
                id="description"
                value={editedItem.description?.[language] || ""}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder={t("descriptionPlaceholder")}
                rows={5}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="price">{t("price")}</Label>
              <div className="flex items-center">
                <div className="bg-muted flex items-center justify-center px-3 py-2 h-10 rounded-l-md border border-r-0 border-input">
                  <span className="text-muted-foreground">{currency}</span>
                </div>
                <Input
                  id="price"
                  value={editedItem.price.amount.toLocaleString()}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  placeholder={t("pricePlaceholder")}
                  className="rounded-l-none"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="show-tax"
                checked={showTaxIncluded}
                onCheckedChange={handleTaxIncludedChange}
              />
              <Label htmlFor="show-tax">{t("showTaxIncluded")}</Label>
            </div>
            {showTaxIncluded && editedItem.price.amount > 0 && (
              <div className="text-sm text-muted-foreground">
                {t("taxIncluded")}: {currency}
                {Math.round(editedItem.price.amount * 1.1).toLocaleString()}
              </div>
            )}

            {supportedLanguages.length > 1 && (
              <div className="space-y-4 pt-2 mb-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="translate"
                    checked={shouldTranslate}
                    onCheckedChange={handleTranslateToggle}
                    disabled={remainingCredits === 0}
                  />
                  <Label
                    htmlFor="translate"
                    className={cn(
                      remainingCredits === 0 ? "text-muted-foreground" : "",
                      "flex-1"
                    )}
                  >
                    {t("translateOtherLanguages")}
                  </Label>
                  {remainingCredits > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {remainingCredits}{" "}
                      {t("credit", { count: remainingCredits })}
                    </span>
                  )}
                </div>

                {remainingCredits === 0 && (
                  <div className="text-sm text-destructive">
                    {t("noCreditsAvailable")}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseCredits")}
                    </Link>
                  </div>
                )}

                {remainingCredits > 0 && remainingCredits < requiredCredits && (
                  <div className="text-sm text-destructive">
                    {t("notEnoughCredits", { requiredCredits })}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseMoreCredits")}
                    </Link>
                  </div>
                )}

                {shouldTranslate && remainingCredits > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t("selectLanguageToTranslate")}</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="select-all-languages"
                          checked={
                            selectedLanguages.length ===
                            Math.min(
                              supportedLanguages.filter(
                                (lang) => lang !== language
                              ).length,
                              remainingCredits
                            )
                          }
                          onCheckedChange={(checked) => {
                            if (checked) {
                              // Select all languages up to the credit limit
                              const availableLanguages =
                                supportedLanguages.filter(
                                  (lang) => lang !== language
                                );
                              const languagesToSelect =
                                availableLanguages.slice(
                                  0,
                                  Math.min(
                                    availableLanguages.length,
                                    remainingCredits
                                  )
                                );
                              setSelectedLanguages(languagesToSelect);
                              setHasChanges(checkForChanges());

                              if (
                                availableLanguages.length > remainingCredits
                              ) {
                                toast({
                                  variant: "default",
                                  title: t("limitedCreditsTitle"),
                                  description: t("limitedCreditsDescription", {
                                    count: remainingCredits,
                                    plural: remainingCredits === 1 ? "" : "s",
                                  }),
                                });
                              }
                            } else {
                              setSelectedLanguages([]);
                              setHasChanges(checkForChanges());
                            }
                          }}
                        />
                        <Label
                          htmlFor="select-all-languages"
                          className="text-sm"
                        >
                          {t("selectAll")}
                        </Label>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {supportedLanguages.map((lang) => {
                        if (lang === language) return null;
                        const langInfo = getLanguageInfo(lang);
                        const countryCode = getCountryCode(lang);
                        const FlagComponent =
                          flags[countryCode as keyof typeof flags];
                        const isDisabled =
                          !selectedLanguages.includes(lang) &&
                          selectedLanguages.length >= remainingCredits;

                        return (
                          <div
                            key={lang}
                            className={cn(
                              "flex items-center p-2 px-4 bg-muted/50 rounded-md",
                              isDisabled && "opacity-50"
                            )}
                          >
                            <Checkbox
                              id={`lang-${lang}`}
                              checked={selectedLanguages.includes(lang)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  if (
                                    selectedLanguages.length + 1 >
                                    remainingCredits
                                  ) {
                                    toast({
                                      variant: "destructive",
                                      title: t("insufficientCredits"),
                                      description: t("notEnoughCreditsMessage"),
                                    });
                                    return;
                                  }
                                  const newSelectedLanguages = [
                                    ...selectedLanguages,
                                    lang,
                                  ];
                                  setSelectedLanguages(newSelectedLanguages);
                                  setHasChanges(checkForChanges());
                                } else {
                                  const newSelectedLanguages =
                                    selectedLanguages.filter((l) => l !== lang);
                                  setSelectedLanguages(newSelectedLanguages);
                                  setHasChanges(checkForChanges());
                                }
                              }}
                              disabled={isDisabled}
                              className="mr-2"
                            />
                            <div className="flex-shrink-0 mx-2">
                              {FlagComponent && (
                                <FlagComponent className="w-6 h-4 rounded shadow-sm" />
                              )}
                            </div>
                            <div className="flex flex-col flex-1 ml-2">
                              <Label
                                htmlFor={`lang-${lang}`}
                                className={cn(
                                  "text-sm font-medium",
                                  isDisabled && "text-muted-foreground"
                                )}
                              >
                                {getLocalizedLanguageName(lang)}
                              </Label>
                              <span className="text-xs text-muted-foreground">
                                {langInfo?.nativeName || lang}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-row pt-4 flex items-center border-t mt-auto flex-shrink-0 p-6">
            <div className="mr-auto">
              {!isNewItem && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleDeleteClick}
                >
                  {t("delete")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {t("cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={
                  isSaving || (!hasChanges && !previewUrl && !selectedFile)
                }
              >
                {isSaving ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                    <span>
                      {isTranslating ? t("translating") : t("saving")}
                    </span>
                  </div>
                ) : (
                  t("save")
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteMenuItem")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation", { itemName: item.name[language] })}{" "}
              <br />
              {t("actionCannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto mt-0">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90 ml-0"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showCancelTranslationAlert}
        onOpenChange={setShowCancelTranslationAlert}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cancelTranslation")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cancelTranslationDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel
              className="w-full sm:w-auto"
              onClick={() => setShowCancelTranslationAlert(false)}
            >
              {t("continueTranslation")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelTranslation}
            >
              {t("cancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
